import { useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * useSceneScrollTimings Hook
 * 
 * Creates GSAP ScrollTrigger animations for scene transitions.
 * Handles scroll-driven animations, pinning, scrubbing, and callbacks.
 * 
 * @param {React.RefObject} sectionRef - Reference to the section DOM element
 * @param {Object} config - ScrollTrigger configuration
 * @param {string} config.start - Start trigger point (default: "top 70%")
 * @param {string} config.end - End trigger point (default: "bottom 40%")
 * @param {boolean|number} config.scrub - Enable scrubbing (default: false)
 * @param {boolean} config.pin - Pin section during animation (default: false)
 * @param {Function} config.onEnter - Callback when entering trigger area
 * @param {Function} config.onLeave - Callback when leaving trigger area
 * @param {Function} config.onUpdate - Callback on scroll progress update
 * @param {string} config.toggleActions - ScrollTrigger toggle actions
 * @param {Object} config.animation - GSAP animation object to link
 * @returns {void}
 * 
 * @example
 * const sectionRef = useRef(null);
 * useSceneScrollTimings(sectionRef, {
 *   start: "top center",
 *   end: "bottom center",
 *   scrub: true,
 *   onEnter: () => console.log('Scene entered'),
 * });
 */
export const useSceneScrollTimings = (sectionRef, config = {}) => {
  useLayoutEffect(() => {
    // Skip if ref not attached or SSR
    if (!sectionRef.current || typeof window === 'undefined') return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // For reduced motion users, trigger callbacks immediately without animation
      if (config.onEnter) config.onEnter();
      return;
    }

    // Create GSAP context for cleanup
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: config.start || 'top 70%',
        end: config.end || 'bottom 40%',
        scrub: config.scrub !== undefined ? config.scrub : false,
        pin: config.pin || false,
        pinSpacing: config.pinSpacing !== false,
        markers: config.markers || false, // Enable for debugging
        toggleActions: config.toggleActions || 'play none none reverse',
        
        // Callbacks
        onEnter: config.onEnter,
        onLeave: config.onLeave,
        onEnterBack: config.onEnterBack,
        onLeaveBack: config.onLeaveBack,
        onUpdate: (self) => {
          if (config.onUpdate) {
            config.onUpdate(self.progress, self);
          }
        },
        
        // Optional animation object
        animation: config.animation,
      });
    }, sectionRef);

    // Cleanup on unmount
    return () => ctx.revert();
  }, [sectionRef, config]);
};

/**
 * useScrollProgress Hook
 * 
 * Tracks scroll progress through a specific element.
 * Returns a number between 0 and 1 representing scroll completion.
 * 
 * @param {React.RefObject} elementRef - Reference to the element to track
 * @param {Function} callback - Callback function receiving progress (0-1)
 * @returns {void}
 * 
 * @example
 * const elementRef = useRef(null);
 * useScrollProgress(elementRef, (progress) => {
 *   console.log(`Scrolled ${progress * 100}%`);
 * });
 */
export const useScrollProgress = (elementRef, callback) => {
  useLayoutEffect(() => {
    if (!elementRef.current || typeof window === 'undefined') return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: elementRef.current,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: (self) => {
          if (callback) callback(self.progress);
        },
      });
    }, elementRef);

    return () => ctx.revert();
  }, [elementRef, callback]);
};